export type PaginationBucket = 'week' | 'month' | 'custom';

type WeekStart = 'mon' | 'sun';

/** YYYY-MM-DD 文字列を返す */
export function toDateStr(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}

/** YYYY-MM 文字列を返す */
export function toMonthKey(d: Date): string {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

/** 月の初日を返す */
function startOfMonth(d: Date): Date {
    return new Date(d.getFullYear(), d.getMonth(), 1);
}

/** 月の末日を返す */
function endOfMonth(d: Date): Date {
    return new Date(d.getFullYear(), d.getMonth() + 1, 0);
}

/** 指定日を含む週の月曜 or 日曜を返す */
function startOfWeek(d: Date, weekStart: WeekStart): Date {
    const day = d.getDay();
    const diff = weekStart === 'mon' ? ((day + 6) % 7) : day;
    const result = new Date(d);
    result.setDate(result.getDate() - diff);
    return result;
}

/** 週の終了日を返す */
function endOfWeek(d: Date, weekStart: WeekStart): Date {
    const s = startOfWeek(d, weekStart);
    s.setDate(s.getDate() + 6);
    return s;
}

export interface PeriodRange {
    start: Date;
    end: Date;
}

/** ローカル日付の始まり（時刻を 0:00 に揃える） */
export function startOfLocalDay(d: Date): Date {
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/** ローカル暦で start〜end を両端含む日数（同日なら 1）。start > end のときは 0 以下になり得る */
export function inclusiveLocalCalendarDayCount(start: Date, end: Date): number {
    const s = startOfLocalDay(start);
    const e = startOfLocalDay(end);
    const t0 = Date.UTC(s.getFullYear(), s.getMonth(), s.getDate());
    const t1 = Date.UTC(e.getFullYear(), e.getMonth(), e.getDate());
    return Math.floor((t1 - t0) / 86400000) + 1;
}

/** ローカル暦で start〜end を両端含む各日の YYYY-MM-DD を昇順で列挙する（start > end のときは空配列） */
export function eachInclusiveLocalDateStr(start: Date, end: Date): string[] {
    const s = startOfLocalDay(start);
    const e = startOfLocalDay(end);
    if (s > e) return [];
    const out: string[] = [];
    const cur = new Date(s);
    while (cur <= e) {
        out.push(toDateStr(cur));
        cur.setDate(cur.getDate() + 1);
    }
    return out;
}

/** 指定日を含む週の期間を返す（ピッカー選択用） */
export function getPeriodContainingDate(d: Date, weekStart: WeekStart = 'mon'): PeriodRange {
    return { start: startOfWeek(d, weekStart), end: endOfWeek(d, weekStart) };
}

/** 指定日を含む月の期間を返す（ピッカー選択用） */
export function getMonthPeriodContainingDate(d: Date): PeriodRange {
    const s = startOfLocalDay(new Date(d.getFullYear(), d.getMonth(), 1));
    const e = startOfLocalDay(new Date(d.getFullYear(), d.getMonth() + 1, 0));
    return { start: s, end: e };
}

type NonCustomBucket = Exclude<PaginationBucket, 'custom'>;

/** 今を含む週／月の期間を返す */
export function getCurrentPeriod(bucket: NonCustomBucket, weekStart: WeekStart = 'mon'): PeriodRange {
    const now = new Date();
    if (bucket === 'month') {
        return getMonthPeriodContainingDate(now);
    }
    return { start: startOfWeek(now, weekStart), end: endOfWeek(now, weekStart) };
}

/** 1つ前の期間を返す（custom のときはそのまま） */
export function getPrevPeriod(current: PeriodRange, bucket: PaginationBucket, weekStart: WeekStart = 'mon'): PeriodRange {
    if (bucket === 'custom') {
        return current;
    }
    if (bucket === 'month') {
        const d = new Date(current.start.getFullYear(), current.start.getMonth() - 1, 1);
        return getMonthPeriodContainingDate(d);
    }
    const prev = new Date(current.start);
    prev.setDate(prev.getDate() - 7);
    return { start: startOfWeek(prev, weekStart), end: endOfWeek(prev, weekStart) };
}

/**
 * 分析用の前期間。週／月は {@link getPrevPeriod} と同じ。
 * カスタムは現在の期間と同じ日数分、ローカル暦で直前の連続区間（終端は現在の開始の前日）。
 */
export function getAnalysisPrevPeriod(
    current: PeriodRange,
    bucket: PaginationBucket,
    weekStart: WeekStart = 'mon',
): PeriodRange {
    if (bucket !== 'custom') {
        return getPrevPeriod(current, bucket, weekStart);
    }
    const s = startOfLocalDay(current.start);
    const e = startOfLocalDay(current.end);
    if (s > e) {
        return { start: s, end: e };
    }
    const n = inclusiveLocalCalendarDayCount(s, e);
    if (n < 1) {
        return { start: s, end: e };
    }
    const prevEnd = new Date(s);
    prevEnd.setDate(prevEnd.getDate() - 1);
    const prevStart = new Date(prevEnd);
    prevStart.setDate(prevStart.getDate() - (n - 1));
    return { start: startOfLocalDay(prevStart), end: startOfLocalDay(prevEnd) };
}

/** 1つ次の期間を返す（custom のときはそのまま） */
export function getNextPeriod(current: PeriodRange, bucket: PaginationBucket, weekStart: WeekStart = 'mon'): PeriodRange {
    if (bucket === 'custom') {
        return current;
    }
    if (bucket === 'month') {
        const d = new Date(current.start.getFullYear(), current.start.getMonth() + 1, 1);
        return getMonthPeriodContainingDate(d);
    }
    const next = new Date(current.start);
    next.setDate(next.getDate() + 7);
    return { start: startOfWeek(next, weekStart), end: endOfWeek(next, weekStart) };
}

/**
 * 初回取得用：前月・当月・翌月の開始日・終了日を返す
 */
export function getInitialFetchRange(): { start: Date; end: Date; months: string[] } {
    const now = new Date();
    const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const next = new Date(now.getFullYear(), now.getMonth() + 1, 1);
    return {
        start: startOfMonth(prev),
        end: endOfMonth(next),
        months: [toMonthKey(prev), toMonthKey(now), toMonthKey(next)],
    };
}

/**
 * 指定月の fetch 範囲を返す
 */
export function getMonthFetchRange(monthKey: string): { start: Date; end: Date } {
    const [y, m] = monthKey.split('-').map(Number);
    const d = new Date(y, m - 1, 1);
    return { start: startOfMonth(d), end: endOfMonth(d) };
}

/**
 * 期間に含まれる月キーを列挙する
 */
export function getMonthKeysInRange(start: Date, end: Date): string[] {
    const keys: string[] = [];
    const cursor = new Date(start.getFullYear(), start.getMonth(), 1);
    while (cursor <= end) {
        keys.push(toMonthKey(cursor));
        cursor.setMonth(cursor.getMonth() + 1);
    }
    return keys;
}

/**
 * 分析ページのウォームアップ取得。
 * 有料は前月〜翌月を1リクエスト。無料は planLimits の1リクエスト日数上限のため月ごとに分割取得する。
 */
export async function prefetchAnalysisWarmupRanges(
    isPaid: boolean,
    fetchRange: (start: Date, end: Date) => Promise<void>,
): Promise<void> {
    const { start, end, months } = getInitialFetchRange();
    if (isPaid) {
        await fetchRange(start, end);
        return;
    }
    for (const mk of months) {
        const r = getMonthFetchRange(mk);
        await fetchRange(r.start, r.end);
    }
}
